/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { QUERY_PERSIST_CACHE, useAppConfig } from '@airasia-rn/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import PassengerClassSelectionConstants from '../components/PassengerClass/constants';
import { TripType } from '../components/TripTypeSelector/types';
import {
  FILTER_DURATIONS,
  JOURNEY_DATE_CONFIG,
  CABIN_CLASS,
  type CabinClassType,
} from '../config/constants';
import { StationDataObject } from '../types/FlightSearch.types';
import { Trip } from '../widgets/FlightCard/types';
import { useFlightConfig } from '../config/FlightConfigProvider';
import { Platform } from 'react-native';

const REWARDS_CURRENCY = ['PTS', 'LPX'];

// --- Constants & Keys ---
const BASE_RECOMMENDATIONS_KEY = [
  QUERY_PERSIST_CACHE,
  'flights',
  'recommendations',
] as const;

export const flightKeys = {
  // Combined state key
  nearestAirportsCodes: [
    QUERY_PERSIST_CACHE,
    'flights',
    'nearestAirportsCodes',
  ],
  stationSearch: ['flights', 'stationSearch'],
  filters: ['flights', 'filters'],
  carriers: ['flights', 'carriers'],
  search: ['flights', 'search'],
  authtoken: ['flights', 'authtoken'],
  recentSearches: [QUERY_PERSIST_CACHE, 'flights', 'recentSearches'],
  lastSearchUrl: [QUERY_PERSIST_CACHE, 'flights', 'lastSearchUrl'],
  bigMemberId: (guestId: string, tripId?: string) => [
    'flights',
    'guest',
    'bigMemberId',
    tripId,
    guestId,
  ],
  memberLookupData: (guestId: string, tripId?: string) => [
    'flights',
    'guest',
    'memberLookupData',
    tripId,
    guestId,
  ],
  lowFare: ['flights', 'lowFare'],
  recommendations: BASE_RECOMMENDATIONS_KEY,
  crossSellRecommendations: [...BASE_RECOMMENDATIONS_KEY, 'cross-sell'],
  upsellBundle: ['flights', 'upsellBundle'],
  managedGuestList: [
    QUERY_PERSIST_CACHE,
    'flights',
    'guest',
    'managedGuestList',
  ],
  // Tracks which pax counts were used to seed managedGuestList.
  // Needed because managedGuestList is client state (not a server query) and
  // must be re-seeded when user changes pax in search.
  managedGuestListSeedPax: [
    QUERY_PERSIST_CACHE,
    'flights',
    'guest',
    'managedGuestListSeedPax',
  ],
  // Marks that the user has manually changed guest selection (e.g. toggled a
  // guest on/off) for the current managedGuestList. Once set, the passive
  // nationality-merge reseed in setInitialGuests must not overwrite guest
  // selection anymore, since every reseed rebuilds the list from scratch
  // (selecting only the first favourite member) and would otherwise silently
  // clobber whatever the user chose.
  managedGuestListUserModified: [
    QUERY_PERSIST_CACHE,
    'flights',
    'guest',
    'managedGuestListUserModified',
  ],
  pseudoFavMembers: ['flights', 'guest', 'pseudoFavMembers'],
  contactDetails: ['flights', 'guest', 'contactDetails'],
  guestFormDraft: (userId: string, guestId: string) => [
    'flights',
    'guest',
    'formDraft',
    userId,
    guestId,
  ],
  contactFormDraft: (userId: string) => [
    'flights',
    'guest',
    'contactFormDraft',
    userId,
  ],
  checkoutGuestSession: ['flights', 'guest', 'checkoutSession'],
  baggageSelections: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'baggageSelections',
  ],
  mealsSelections: [QUERY_PERSIST_CACHE, 'flights', 'addon', 'mealsSelections'],
  mealsSelectionsDraft: ['flights', 'addon', 'mealsSelectionsDraft'],
  mealsSelectionsPostbooking: [
    QUERY_PERSIST_CACHE,
    'flights',
    'mmb',
    'addon',
    'mealsSelections',
  ],
  mealsSelectionsPostbookingDraft: [
    'flights',
    'mmb',
    'addon',
    'mealsSelectionsDraft',
  ],
  seatSelections: [QUERY_PERSIST_CACHE, 'flights', 'addon', 'seatSelections'],
  seatSelectionsDraft: ['flights', 'addon', 'seatSelectionsDraft'],
  insuranceSelections: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'insuranceSelections',
  ],
  carbonOffsetSelection: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'carbonOffsetSelection',
  ],
  fastPassSelection: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'fastPassSelection',
  ],
  bookingStepper: [QUERY_PERSIST_CACHE, 'flights', 'guest', 'bookingStepper'],
  bundleSelections: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'bundleSelections',
  ],
  fareSummary: [QUERY_PERSIST_CACHE, 'flights', 'guest', 'fareSummary'],
  // In-memory only (no QUERY_PERSIST_CACHE): a reload re-detects sold outs.
  bundleSeatSoldOutJourneys: ['flights', 'addon', 'bundleSeatSoldOutJourneys'],
  baggageSelectionsDraft: ['flights', 'addon', 'baggageSelectionsDraft'],
  wheelchairSelections: [
    QUERY_PERSIST_CACHE,
    'flights',
    'addon',
    'wheelchairSelections',
  ],
  wheelchairSelectionsStaging: [
    'flights',
    'addon',
    'wheelchairSelectionsStaging',
  ],
  fastPassSelectionDraft: ['flights', 'addon', 'fastPassSelectionDraft'],
  companyDetails: ['flights', 'guest', 'companyDetails'],
  addonContainer: ['flights', 'addon', 'container'],
  addonContainerSelection: ['flights', 'addon', 'selection'],
  currency: ['flights', 'guest', 'currency'],
  guestAggregator: [QUERY_PERSIST_CACHE, 'flights', 'guest', 'aggregator'],
  mmbGuestAggregator: ['flights', 'mmb', 'guest', 'aggregator'],
  mmbCheckout: ['flights', 'mmb', 'checkout'],
  mmbAncillaryHydration: (
    ancillaryType: 'baggage' | 'seat' | 'fastpass',
    bookingKey?: string,
  ) => [
    QUERY_PERSIST_CACHE,
    'flights',
    'mmb',
    'ancillaryHydration',
    ancillaryType,
    bookingKey ?? 'unknown-booking',
  ],
  mmbAncillarySelectionScope: [
    QUERY_PERSIST_CACHE,
    'flights',
    'mmb',
    'ancillarySelectionScope',
  ],
  currencyFormat: ['flights', 'price', 'currencyFormat'],
  xsellJwt: ['flights', 'xsell', 'jwt'],
  xsellOmsKey: ['flights', 'xsell', 'omsKey'],
  xsellAggregator: ['flights', 'xsell', 'aggregator'],
  stateProvince: ['flights', 'guest', 'stateProvince'],
  searchAggregatorDeeplink: ['flights', 'searchAggregatorDeeplink'],
  isGuestYpta: ['flights', 'guest', 'isGuestYpta'],
  guestFormNavigationHandoff: ['flights', 'guest', 'formNavigation', 'handoff'],
  checkout: ['flights', 'guest', 'checkout'],
  manageBooking: ['flights', 'manageBooking'],
  multiCartContext: ['flights', 'multiCartContext'],
  changeFlightKey: (key?: string) => [
    'flights',
    'changeFlight',
    'key',
    key ?? '',
  ],
  changeFlightOptions: (
    key?: string,
    departDate?: string,
    returnDate?: string,
  ) => [
    'flights',
    'changeFlight',
    'options',
    key ?? '',
    departDate,
    returnDate,
  ],
};

// --- Types ---
export interface SearchFlightFilters {
  // Search Criteria
  tripType: TripType;
  originObj: StationDataObject | undefined;
  destinationObj: StationDataObject | undefined;
  promoCode?: string;
  isAirAsiaFlightsOnly: boolean;
  isCheckBagsIncluded: boolean;
  isDirectFlights: boolean;
  isSnapCheckbox: boolean;
  departDate: Date | undefined;
  returnDate?: Date;
  adultCount: number;
  childCount: number;
  infantCount: number;
  cabinClass: CabinClassType;
  page: number;
  sort?: string;
  selectedDepartFlight?: Trip;
  selectedReturnFlight?: Trip;
  applyMixedClasses: boolean;
  stopType: string;
  allowOvernight: boolean;
  maxTravelTimeInHrs: number; //journey duration
  maxStopoverTimeInHrs: number; //layover duration
  minStopoverTimeInHrs: number;
  allowAllCarriers: boolean;
  onlyAllowedCarriers: string[]; //airlines filter
  excludedCarriers: string[] | null;
  // UI only (never sent to the API): distinguishes "All airlines" explicitly ticked
  // from the blank default. Both use allowAllCarriers=true in the payload.
  isAllCarriersSelected?: boolean;
  allowAllAirports: boolean;
  allowedDepartAirports: string[] | null;
  allowAllReturnAirports: boolean;
  allowedReturnAirports: string[] | null;
  didEditSearch?: boolean;
  departPointsPlusCashEnabled?: boolean;
  returnPointsPlusCashEnabled?: boolean;
}

// --- Defaults ---
export const defaultSearchFlightFilters: SearchFlightFilters = {
  // Search Defaults
  tripType: TripType.ROUND_TRIP,
  originObj: undefined,
  destinationObj: undefined,
  promoCode: undefined,
  isAirAsiaFlightsOnly: false,
  isCheckBagsIncluded: false,
  isSnapCheckbox: false,
  isDirectFlights: false,
  departDate: JOURNEY_DATE_CONFIG.departDate,
  returnDate: JOURNEY_DATE_CONFIG.returnDate,
  adultCount: PassengerClassSelectionConstants.DEFAULT_ADULT_COUNT,
  childCount: PassengerClassSelectionConstants.DEFAULT_CHILD_COUNT,
  infantCount: PassengerClassSelectionConstants.DEFAULT_INFANT_COUNT,
  cabinClass: CABIN_CLASS.ECONOMY,
  page: 1,
  sort: undefined,
  selectedDepartFlight: undefined,
  selectedReturnFlight: undefined,
  applyMixedClasses: false,
  stopType: 'ANY',
  allowOvernight: false,
  maxTravelTimeInHrs: FILTER_DURATIONS.maxJourneyDuration,
  maxStopoverTimeInHrs: FILTER_DURATIONS.maxStopoverDuration,
  minStopoverTimeInHrs: FILTER_DURATIONS.minStopoverDuration,

  allowAllCarriers: true,
  onlyAllowedCarriers: [],
  excludedCarriers: [],
  isAllCarriersSelected: false,

  allowAllAirports: true,
  allowedDepartAirports: [],

  allowAllReturnAirports: true,
  allowedReturnAirports: [],

  didEditSearch: false,

  departPointsPlusCashEnabled: false,
  returnPointsPlusCashEnabled: false,
};

// export interface Flight extends SearchFlightFilters {
//   // @@ TO DO:
//   // Add other fields as needed based on API response
//   id: string;
//   airline: string;
//   price: number;
// }

// --- Hooks ---
/**
 * Hook to get current flight search and filter criteria (Client State)
 */
export const useSearchFlightsFilters = () => {
  return useQuery<SearchFlightFilters>({
    queryKey: flightKeys.filters,
    queryFn: () => defaultSearchFlightFilters,
    initialData: defaultSearchFlightFilters,
    staleTime: Infinity,
    // gcTime: Infinity,
  });
};

/**
 * Hook to update flight search and filter criteria
 */
export const useSearchFlightsFiltersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newFilters: Partial<SearchFlightFilters>) => {
      // No server call — this mutation only updates client cache (patch === newFilters)
      return newFilters;
    },
    onMutate: (newFilters) => {
      const previous =
        queryClient.getQueryData<SearchFlightFilters>(flightKeys.filters) ??
        defaultSearchFlightFilters;

      const updated: SearchFlightFilters = {
        ...defaultSearchFlightFilters,
        ...previous,
        ...newFilters,
      };

      queryClient.setQueryData<SearchFlightFilters>(
        flightKeys.filters,
        updated,
      );

      return { previousFilters: previous };
    },
    onError: (err, newFilters, context) => {
      if (context?.previousFilters) {
        queryClient.setQueryData<SearchFlightFilters>(
          flightKeys.filters,
          context.previousFilters,
        );
      }
    },
  });
};

export const useInsertParamsInUrl = () => {
  const { config } = useAppConfig();
  const { config: flightConfig } = useFlightConfig();
  const currency = flightConfig.currency || config.currency;

  const insertParamsInUrl = useCallback(
    (url: string, params: string[]) => {
      const upper = currency?.toUpperCase() ?? '';
      const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
      const displayCurrency =
        isNative && upper === REWARDS_CURRENCY[1] ? REWARDS_CURRENCY[0] : upper;

      // Swap sequence {0} {1} to {1} {0} for PTS (suffix currency placement)
      let targetUrl = url;
      if (displayCurrency === REWARDS_CURRENCY[0]) {
        targetUrl = url.replace(/\{0\}\s*\{1\}/g, '{1} {0}');
      }

      const finalParams = [displayCurrency, ...params];

      return targetUrl.replace(/\{(\d+)\}/g, (match, indexStr) => {
        const index = Number(indexStr);
        return finalParams[index] !== undefined ? finalParams[index] : match;
      });
    },
    [currency],
  );

  return insertParamsInUrl;
};
